/**
 * SSE Client — POST-based streaming via fetch + ReadableStream.
 * Layer: C (UX/CLI)
 * Spec: §5.3.1 — SSE over WebSocket (D-CHAT-SSE)
 *
 * EventSource only supports GET. We need POST with body → fetch + stream.
 */

import { getAccessToken } from './api';

const BASE = import.meta.env.VITE_API_URL ?? '';

export interface StreamEvent {
  type: string;
  text?: string;
  tool?: {
    id: string;
    name: string;
    args?: Record<string, unknown>;
    result?: unknown;
    status?: 'pending' | 'running' | 'success' | 'error';
  };
  usage?: { input_tokens: number; output_tokens: number; cost_usd: number };
  model?: string;
  error?: { code: string; message: string };
}

export interface StreamChatOptions {
  conversationId?: string | null;
  content: string;
  model?: string;
  signal?: AbortSignal;
  onEvent: (event: StreamEvent) => void;
  onDone: () => void;
  onError: (error: Error) => void;
}

export async function streamChat(opts: StreamChatOptions): Promise<void> {
  const { conversationId, content, model, signal, onEvent, onDone, onError } = opts;

  const token = getAccessToken();
  if (!token) {
    onError(new Error('Inte inloggad.'));
    return;
  }

  try {
    const res = await fetch(`${BASE}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        conversation_id: conversationId ?? undefined,
        content,
        model: model ?? undefined,
      }),
      signal,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      onError(new Error((data as Record<string, string>).message ?? `HTTP ${res.status}`));
      return;
    }

    if (!res.body) {
      onError(new Error('Inget svar från servern.'));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // SSE data line
        if (trimmed.startsWith('data: ')) {
          const data = trimmed.slice(6).trim();
          if (data === '[DONE]') {
            onDone();
            return;
          }
          try {
            const event = JSON.parse(data) as StreamEvent;
            onEvent(event);
          } catch {
            // Ignore malformed JSON
          }
        }
        // Ignore event: lines (we parse data: directly)
      }
    }

    onDone();
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    onError(err as Error);
  }
}
